import React, { useCallback } from 'react';
import { Button, Col, Input, Label, Row } from 'reactstrap';
import { isNumber, Storage, translate, Translate, ValidatedField } from 'react-jhipster';
import Draggable from 'react-draggable';
import { useForm } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

interface FeeDialogProps {
  open: boolean;
  isNew: boolean;
  fromValue: number;
  toValue: string;
  checkedToValue: number;
  noDecimalAllowed: boolean;
  selectedFeeType: string;
  validateFromTo: string;
  transactionAmountLimit: number;
  loading: boolean;
  updating: boolean;
  uniqueFromAmountFee: any[];
  feeForm: React.ReactNode;
  onClose: () => void;
  onSubmit: (values: any) => void;
  onFromValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFeeTypeChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onFormStateChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  commodityType: 'International' | 'Local';
}

function toEnglishDigits(str: string): string {
  let e = '۰'.charCodeAt(0);
  str = str.replace(/[۰-۹]/g, function (t) {
    return String(t.charCodeAt(0) - e);
  });

  e = '٠'.charCodeAt(0);
  str = str.replace(/[٠-٩]/g, function (t) {
    return String(t.charCodeAt(0) - e);
  });
  return str;
}

export const BankDetailFeeDialog: React.FC<FeeDialogProps> = React.memo(
  ({
    open,
    isNew,
    fromValue,
    toValue,
    checkedToValue,
    noDecimalAllowed,
    selectedFeeType,
    validateFromTo,
    transactionAmountLimit,
    loading,
    updating,
    uniqueFromAmountFee,
    feeForm,
    onClose,
    onSubmit,
    onFromValueChange,
    onToValueChange,
    onFeeTypeChange,
    onFormStateChange,
    commodityType,
  }) => {
    const {
      handleSubmit,
      register,
      formState: { errors },
    } = useForm({ mode: 'onTouched' });

    const handleDigits = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const value = toEnglishDigits(event.target.value);
        const cleanedValue = value.replace(/[^0-9]+/g, '');

        const syntheticEvent = {
          ...event,
          target: {
            ...event.target,
            value: cleanedValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onFormStateChange(id)(syntheticEvent);
      },
      [onFormStateChange]
    );

    if (loading || updating) {
      return (
        <Dialog open={open} onClose={onClose}>
          <DialogTitle style={{ fontWeight: 'regular' }}>Loading</DialogTitle>
        </Dialog>
      );
    }

    return (
      <Draggable handle=".dialog-title-handle">
        <Dialog
          open={open}
          onClose={onClose}
          hideBackdrop={true}
          maxWidth="sm"
          fullWidth={true}
          sx={Storage.session.get('locale') === 'ar-ly' ? { marginLeft: '40px' } : null}
        >
          <DialogTitle className="dialog-title-handle" style={{ fontWeight: 'bold', cursor: 'move' }}>
            <Grid container spacing={2}>
              <Grid item xs={10} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                {isNew ? (
                  <Translate contentKey="comexApp.bank.details.addNewFeeRange" />
                ) : (
                  <Translate contentKey="comexApp.bank.details.updateFeeRange" />
                )}
              </Grid>
            </Grid>
          </DialogTitle>
          <form
            onSubmit={e => {
              e.preventDefault();
              onSubmit({});
            }}
          >
            <DialogContent>
              <Grid sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    position: 'absolute',
                    top: 10,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
              <DialogContentText style={{ color: 'black' }} className="mb-3" component={'span'}>
                {!isNew && (
                  <ValidatedField
                    name="id"
                    register={register}
                    hidden
                    readOnly
                    id={`comex-bank-fee-id-${commodityType.toLowerCase()}`}
                    label={translate('global.field.id')}
                    validate={{ required: true }}
                  />
                )}

                <Row className="justify-content-center">
                  <Col xs={6}>
                    <Label>
                      <Translate contentKey="comexApp.bank.details.from" />
                    </Label>
                    <Input type="select" value={fromValue} onChange={onFromValueChange} disabled={!isNew}>
                      <option value={1} key={1}>
                        1
                      </option>
                      {uniqueFromAmountFee?.map(fee => (
                        <option value={fee.fromTo + 1} key={fee.fromTo + 1}>
                          {(fee.fromTo + 1).toLocaleString()}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col xs={6}>
                    <ValidatedField
                      label={translate('comexApp.bank.details.to')}
                      id={`comex-bank-fee-fromTo-${commodityType.toLowerCase()}`}
                      register={register}
                      error={errors?.fromTo}
                      name="fromTo"
                      data-cy="fromTo"
                      type="text"
                      onChange={onToValueChange}
                      value={toValue}
                      validate={{
                        required: { value: true, message: translate('entity.validation.required') },
                        // eslint-disable-next-line object-shorthand
                        validate: v => {
                          if (checkedToValue > transactionAmountLimit) {
                            return translate('comexApp.bank.validation.toValidationMessage', {
                              limit: transactionAmountLimit,
                            });
                          }
                          if (!isNumber(checkedToValue) || checkedToValue <= fromValue) {
                            return translate('entity.validation.number');
                          }
                          return true;
                        },
                      }}
                    />
                    <div className="bank-fee-no-decimal-error" hidden={noDecimalAllowed}>
                      {translate('comexApp.bank.validation.noDecimalAllowed')}
                    </div>
                    {validateFromTo && (
                      <div className="text-muted small mt-1">{translate('comexApp.bank.validation.valueRange', { validateFromTo })}</div>
                    )}
                  </Col>
                </Row>

                <ValidatedField
                  label={translate('comexApp.bank.details.feeType')}
                  id={`comex-bank-fee-feeType-${commodityType.toLowerCase()}`}
                  register={register}
                  error={errors?.feeType}
                  name="feeType"
                  data-cy="feeType"
                  type="select"
                  value={selectedFeeType}
                  onChange={onFeeTypeChange}
                  validate={{
                    required: { value: true, message: translate('entity.validation.required') },
                  }}
                >
                  <option value={'Lump Sum'} key={'Lump Sum'}>
                    {translate('comexApp.bank.details.lumpSum')}
                  </option>
                  <option value={'Percentage'} key={'Percentage'}>
                    {translate('comexApp.bank.details.percentage')}
                  </option>
                </ValidatedField>

                <br />
                <div>
                  <Label>{translate('comexApp.bank.details.amount')}</Label>
                  {feeForm}
                </div>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }} className="mx-3 mb-3">
              <Button onClick={onClose}>
                <Translate contentKey="comexApp.bank.details.cancel" />
              </Button>
              <Button
                color="primary"
                type="submit"
                style={Storage.session.get('locale') === 'en' ? { marginLeft: '10px' } : { marginRight: '10px' }}
                disabled={updating}
              >
                {isNew ? <Translate contentKey="comexApp.bank.details.add" /> : <Translate contentKey="comexApp.bank.details.update" />}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Draggable>
    );
  }
);

BankDetailFeeDialog.displayName = 'BankDetailFeeDialog';
